import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import Validator from 'mixins/validator';
import { Button, Dropdown } from 'react-toolbox';
import { connect } from 'react-redux';
import styles from 'styles/input.scss';
import { DISPLAY_DATE_TIME } from 'config/constants';
import {
  JOB_PAPERWORK_TEMPLATE_LABELS, JOB_PAPERWORK_TEMPLATE_OPTIONS,
} from '../../components/jobPaperworkTemplateTypes';
import EntryDoorTemplate from './templates/EntryDoorTemplate';
import InteriorDoorTemplate from './templates/InteriorDoorTemplate';
import StormDoorTemplate from './templates/StormDoorTemplate';
import WindowsTemplate from './templates/WindowsTemplate';
import WorkorderTemplate from './templates/WorkorderTemplate';
import {
  clearValidation, jobPaperworkTemplateCreate, loadPhase, navigate,
  requestJobPaperworkTemplates,
} from 'actionCreators';
import { validateBarnDoorMeasures } from './templates/components/measures/BarnDoorMeasures';
import { validateEntryDoorMeasures } from './templates/components/measures/EntryDoorMeasures';
import { validateInteriorDoorMeasures } from './templates/components/measures/InteriorDoorMeasures';
import { validateStormDoorMeasures } from './templates/components/measures/StormDoorMeasures';
import { validateWindowMeasures } from './templates/components/measures/WindowMeasures';
import { validateWorkOrderDetails } from './templates/components/WorkOrderDetails';
import { validateArchesTransoms } from './templates/components/ArchesTransoms';
import { validateLaborMaterials } from './templates/components/LaborMaterial';
import { validateProjectNeeds } from './templates/components/ProjectNeeds';
import PhaseType from 'types/phase';
import { urls } from 'helpers/url';
import { canAccessIfiTemplate } from '../../../../../../../services';

const isBlank = value =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '');

function addError(errors, key, message) {
  const nextErrors = { ...errors };
  const currentMessages = nextErrors[key] || [];
  nextErrors[key] = currentMessages.concat(message);
  return nextErrors;
}

function validateAssessmentMeasureRows(
  measures, templateLabel, requiredFields, allowEmpty = false,
) {
  const errors = {};

  if (!Array.isArray(measures)) {
    if (allowEmpty) return errors;
    errors.requiredMeasures = [`At least one ${templateLabel} measure is required.`];
    return errors;
  }
  if (measures.length === 0) {
    if (allowEmpty) return errors;
    errors.requiredMeasures = [`At least one ${templateLabel} measure is required.`];
    return errors;
  }

  measures.forEach((measure, idx) => {
    requiredFields.forEach((fieldPath) => {
      const fieldParts = fieldPath.split('.');
      const value = fieldParts.reduce((obj, key) => (obj ? obj[key] : undefined), measure);
      if (isBlank(value)) {
        Object.assign(errors, addError(
          errors,
          `required.${idx}.${fieldPath}`,
          `${templateLabel} opening #${idx + 1}: ${fieldPath} is required.`,
        ));
      }
    });
  });

  return errors;
}

function validateRequiredTemplateData(templateType, templateData = {}) {
  const errors = {};
  const { measures = {}, details = {} } = templateData;

  switch (templateType) {
    case 'entry_door_worksheet':
      return validateAssessmentMeasureRows(measures.entry_doors, 'Entry door', [
        'location',
        'configuration',
        'jamb_size',
        'unitSize.width',
        'unitSize.height',
        'roughOpening.width',
        'roughOpening.height',
        'handing',
      ]);
    case 'interior_door_worksheet': {
      const interiorDoors = measures.interior_doors || [];
      const barnDoors = measures.barn_doors || [];

      if (!interiorDoors.length && !barnDoors.length) {
        errors.requiredMeasures = [
          'At least one interior door or barn door measure is required.',
        ];
        return errors;
      }

      const interiorErrors = validateAssessmentMeasureRows(interiorDoors, 'Interior door', [
        'location',
        'configuration',
        'jamb_size',
        'unitSize.width',
        'unitSize.height',
        'finishedOpening.width',
        'finishedOpening.height',
        'handing',
      ], true);
      const barnErrors = validateAssessmentMeasureRows(barnDoors, 'Barn door', [
        'location',
        'singleOrDouble',
        'finishedOpening.width',
        'finishedOpening.height',
        'hardwareTrack.width',
        'hardwareTrack.height',
      ], true);

      return { ...interiorErrors, ...barnErrors };
    }
    case 'storm_door_worksheet':
      return validateAssessmentMeasureRows(measures.storm_doors, 'Storm door', [
        'configuration',
        'opening.width',
        'opening.height',
        'sizing',
        'handing',
      ]);
    case 'window_worksheet':
      return validateAssessmentMeasureRows(measures.windows, 'Window', [
        'location',
        'qty',
        'configuration',
        'frameType',
        'frameDepth',
        'unitSize.width',
        'unitSize.height',
        'roughOpening.width',
        'roughOpening.height',
      ]);
    case 'workorder_inspection': {
      const requiredDetailFields = [
        'customerName',
        'job_id',
        'dateOfInspection',
        'location',
        'natureOfIssue',
        'resolution',
        'propertyDamage',
        'productRequired',
        'laborRequired',
      ];
      requiredDetailFields.forEach((field) => {
        if (isBlank(details[field])) {
          Object.assign(errors, addError(errors, `details.${field}`, `${field} is required.`));
        }
      });
      return errors;
    }
    default:
      return {};
  }
}

function validateTemplateData(templateType, templateData) {
  const requiredFieldErrors = validateRequiredTemplateData(templateType, templateData);
  // validations for all templates
  const projectNeedsErrors = validateProjectNeeds(templateData.needs || {});
  let errors = {};

  // validations specific to template type
  switch (templateType) {
    case 'entry_door_worksheet': {
      const { measures = {}, arches_transoms_measures = [], labors_materials = [] } = templateData;
      errors = {
        ...validateEntryDoorMeasures(measures.entry_doors || []),
        ...validateArchesTransoms(arches_transoms_measures, (measures.entry_doors || []).length, 'entry door'),
        ...validateLaborMaterials(labors_materials),
      };
      break;
    }
    case 'interior_door_worksheet': {
      const { measures = {}, labors_materials = [] } = templateData;
      errors = {
        ...validateInteriorDoorMeasures(measures.interior_doors || []),
        ...validateBarnDoorMeasures(measures.barn_doors || []),
        ...validateLaborMaterials(labors_materials),
      };
      break;
    }
    case 'storm_door_worksheet': {
      const { measures = {}, labors_materials = [] } = templateData;
      errors = {
        ...validateStormDoorMeasures(measures.storm_doors || []),
        ...validateLaborMaterials(labors_materials),
      };
      break;
    }
    case 'window_worksheet': {
      const { measures = {}, arches_transoms_measures = [], labors_materials = [] } = templateData;
      errors = {
        ...validateWindowMeasures(measures.windows || []),
        ...validateArchesTransoms(arches_transoms_measures, (measures.windows || []).length, 'window'),
        ...validateLaborMaterials(labors_materials),
      };
      break;
    }
    case 'workorder_inspection': {
      const { details = {} } = templateData;
      errors = {
        ...validateWorkOrderDetails(details),
      };
      break;
    }
    default:
      errors = {};
  }
  // Merge errors
  return { ...requiredFieldErrors, ...errors, ...projectNeedsErrors };
}

const COPY_EXISTING_TEMPLATE = '__copy_existing_template__';

function buildCopySourceLabel(template) {
  const typeLabel = JOB_PAPERWORK_TEMPLATE_LABELS[template.template_type] || template.template_type;
  const dateLabel = moment(template.created_at).format(DISPLAY_DATE_TIME);
  return `${typeLabel} — ${dateLabel}${template.not_use ? ' (not active)' : ''}`;
}

class IfiTemplatePage extends Validator(React.Component) {
  static DEFAULT_STATE = {
    templateType: '',
    permitRequired: false,
    errors: {},
    submitting: false,
    imagesLoading: 0,
    templateData: {
      measures: {
        windows: [], entry_doors: [], storm_doors: [], interior_doors: [], barn_doors: [],
      }, // for assessments
      arches_transoms_measures: [], // for assessments
      labors_materials: [], // for assessments
      comments: '', // for assessments
      needs: {}, // for all templates
      details: {}, // for workorders
      // home_diagram: '',
      // product_template: {},
    },
    templates: [],
    copyMode: false,
    copySourceId: '',
  };

  static VALIDATIONS = { templateType: true }

  constructor(props) {
    super(props);
    const { phase_specific: {
      millwork_specifics: { permit_required = false } = {},
    } = {} } = props.phase;
    this.state = { ...IfiTemplatePage.DEFAULT_STATE, permitRequired: permit_required };
  }

  componentWillMount = () => {
    const { phase, role } = this.props;
    if (!canAccessIfiTemplate({ role, phase_type: phase.type, phase_source: phase.source })) {
      this.props.navigate(urls.phaseHistory(phase));
    }
  }

  componentDidMount = () => {
    const { phase, role } = this.props;
    if (!canAccessIfiTemplate({ role, phase_type: phase.type, phase_source: phase.source })) return;
    requestJobPaperworkTemplates(phase.id).then(({ templates, error }) => {
      if (error) return;
      this.setState({ templates });
    });
  }

  getTemplateComponent = () => {
    switch (this.state.templateType) {
      case 'window_worksheet':
        return WindowsTemplate;
      case 'entry_door_worksheet':
        return EntryDoorTemplate;
      case 'storm_door_worksheet':
        return StormDoorTemplate;
      case 'interior_door_worksheet':
        return InteriorDoorTemplate;
      case 'workorder_inspection':
        return WorkorderTemplate;
      default:
        return null;
    }
  };

  goToHistory = () => {
    this.props.clearValidation();
    this.props.navigate(urls.phaseHistory(this.props.phase));
  }

  handleCancel = () => this.goToHistory();

  handleImageReadStart = () => this.setState(s => ({ imagesLoading: s.imagesLoading + 1 }));

  handleImageReadEnd = () => this.setState(s => ({
    imagesLoading: Math.max(0, s.imagesLoading - 1),
  }));

  handleTemplateTypeChange = (value) => {
    if (value === COPY_EXISTING_TEMPLATE) {
      this.setState({ copyMode: true, templateType: '', copySourceId: '' });
      return;
    }
    const templateData = { ...IfiTemplatePage.DEFAULT_STATE.templateData };
    if (value === 'workorder_inspection') {
      templateData.details = {
        ...IfiTemplatePage.DEFAULT_STATE.templateData.details,
        job_id: this.props.phase.project_number,
        propertyDamage: false,
        productRequired: false,
        laborRequired: false,
      };
    }
    this.setState({
      copyMode: false,
      templateType: value,
      copySourceId: '',
      templateData,
    });
  };

  handleCopySourceChange = (value) => {
    const source = this.state.templates.find(t => t.id === value);
    if (!source) return;
    this.setState({
      copySourceId: value,
      templateType: source.template_type,
      permitRequired: !!source.permit_required,
      templateData: source.template_data || IfiTemplatePage.DEFAULT_STATE.templateData,
    });
  };

  handlePermitRequiredChange = value => this.setState({ permitRequired: value });

  handleTemplateDataChange = data => this.setState({ templateData: data });

  handleSubmit = () => {
    const { copyMode } = this.state;
    const validated = copyMode
      ? this.validate({ copySourceId: 'Please select an existing template to copy from.' })
      : this.validate();
    if (!validated) return;

    const { phase } = this.props;
    const { templateType, permitRequired, templateData } = this.state;

    // Centralized validation
    const errors = validateTemplateData(templateType, templateData);

    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    const templatePayload = {
      phase_id: phase.id,
      template_type: templateType,
      permit_required: permitRequired,
      template_data: templateData,
    };

    this.setState({ submitting: true });
    this.props.jobPaperworkTemplateCreate(templatePayload)
      .then(() => this.props.loadPhase(phase.id).catch(() => null))
      .then(() => {
        this.setState({ submitting: false });
        this.goToHistory();
      })
      .catch(() => {
        this.setState({
          submitting: false,
          errors: { submit: ['Unable to create template. Please try again.'] },
        });
      });
  }

  render = () => {
    const { phase, role } = this.props;
    if (!canAccessIfiTemplate({ role, phase_type: phase.type, phase_source: phase.source })) {
      return null;
    }

    const {
      templateType, templateData, permitRequired, submitting, imagesLoading, errors,
      templates, copyMode, copySourceId,
    } = this.state;
    const templateError = ((errors || {}).templateType || [])[0];
    const copySourceError = ((errors || {}).copySourceId || [])[0];
    const templateOptions = JOB_PAPERWORK_TEMPLATE_OPTIONS[phase.type] || [];
    const topDropdownOptions = templates.length > 0
      ? [...templateOptions, { value: COPY_EXISTING_TEMPLATE, label: 'Copy Existing Template' }]
      : templateOptions;
    const topDropdownValue = copyMode ? COPY_EXISTING_TEMPLATE : templateType;
    const copySourceOptions = templates
      .slice()
      .sort((a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf())
      .map(t => ({ value: t.id, label: buildCopySourceLabel(t) }));
    const TemplateComponent = this.getTemplateComponent();
    const isWorkorderInspection = templateType === 'workorder_inspection';
    const submitLabel = submitting ? 'Creating...' : 'Submit';
    const laborMaterialErrors = validateLaborMaterials(templateData.labors_materials || []);
    const hasLaborMaterialErrors = Object.keys(laborMaterialErrors).length > 0;

    return (<div style={{ padding: '20px' }}>
      <div className={styles.errored}>
        {errors.submit && <span className={styles.error}>{errors.submit[0]}</span>}
      </div>
      <h5>{isWorkorderInspection ? 'Workorder Inspection' : 'IFI Template'}</h5>
      {!isWorkorderInspection && <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h6 style={{ paddingBottom: '10px' }}>Please have all diagram and
          template photos accessible before completing worksheet</h6>
        <li style={{ fontSize: '12px', listStyle: 'none' }}>Home Drawings —
          Drawing/image showing the layout of the home with product placement locations.</li>
        <li style={{ fontSize: '12px', listStyle: 'none' }}>Product Template — Reference image
          for highly configurable products, complex measurements, or product
          specifications.</li>
      </div>}
      <Dropdown
        label="Select Template"
        value={topDropdownValue}
        onChange={this.handleTemplateTypeChange}
        source={topDropdownOptions}
        required
        error={templateError}
      />
      {copyMode && (
        <Dropdown
          label="Copy From"
          value={copySourceId}
          onChange={this.handleCopySourceChange}
          source={copySourceOptions}
          required
          error={copySourceError}
        />
      )}
      {TemplateComponent && (<TemplateComponent
        data={templateData || null}
        onChange={this.handleTemplateDataChange}
        permitRequired={permitRequired}
        onPermitRequiredChange={this.handlePermitRequiredChange}
        onReadStart={this.handleImageReadStart}
        onReadEnd={this.handleImageReadEnd}
        canViewPriceAdjustment={this.props.role !== 'installer'}
      />)}
      {Object.keys(errors).length > 0 && (
        <div style={{ color: 'red', marginBottom: 16 }}>
          {Object.entries(errors).map(([field, msgs]) => {
            if (field === 'submit') return null;
            if (Array.isArray(msgs)) return msgs.map(msg => (<div key={`${field}-${msg}`}>{msg}</div>));
            // If msgs is an object (nested errors), flatten and display
            if (typeof msgs === 'object' && msgs !== null) {
              return Object.entries(msgs).map(([subField, subMsgs]) => (Array.isArray(subMsgs)
                ? subMsgs.map(subMsg => (<div key={`${field}-${subField}-${subMsg}`}>{subMsg}</div>))
                : <div key={`${field}-${subField}`}>{subMsgs}</div>));
            }
            return <div key={field}>{msgs}</div>;
          })}
        </div>
      )}
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <Button label="Cancel" onClick={this.handleCancel} disabled={submitting} />
        <Button
          label={imagesLoading > 0 ? 'Processing image...' : submitLabel}
          primary
          raised
          onClick={this.handleSubmit}
          disabled={submitting || imagesLoading > 0 || hasLaborMaterialErrors}
        />
      </div>
    </div>);
  }
}

IfiTemplatePage.propTypes = {
  clearValidation: PropTypes.func.isRequired,
  jobPaperworkTemplateCreate: PropTypes.func.isRequired,
  loadPhase: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  phase: PhaseType.isRequired,
  role: PropTypes.string.isRequired,
};

export default connect(
  state => ({ phase: state.phase.details, role: state.user.role }),
  { clearValidation, jobPaperworkTemplateCreate, loadPhase, navigate },
)(IfiTemplatePage);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/ifiTemplate/index.jsx